/**
 * =========================================================================================
 * Component - Alert Provider
 * =========================================================================================
 * Alert Provider
 **/

import React, {useCallback, useEffect, useRef, useState, createContext, useContext, useMemo} from 'react';


import Popup from "reactjs-popup";
import * as Icon from 'ubs-icons';
import {COLOR} from '../common/constant'
import UInput from "ubs-framework/v2/UInput";
import Validator from "simple-react-validator";
import { useForceUpdate } from "../hooks";
import { errorFocus } from "../common/function";
import UButton from "../UButton";
import { valid } from "../common/constant";
import UCheckBox from "../UCheckBox";

export const Context = createContext();

export const useUAlert = propsContext => {
    const alertContext = useContext(propsContext || Context)
    return useMemo(() => {
        return alertContext
    }, [alertContext])
}

export const UAlertProvider = (props) => {
    const [alert, setAlert] = useState(null)
    const [commentAlert, setCommentAlert] = useState(null)
    const forceUpdate = useForceUpdate();
    const validator = useRef(new Validator({ element: (message, className) => <div className={'u_error'}>{message}</div>, autoForceUpdate: { forceUpdate } }));
    const [word, setWord] = useState('')
    const [isSync, setIsSync] = useState(false)
    // When an alert opts in with `asyncSubmit`, its onSubmit may return a Promise. We keep the alert
    // open with a loader on the submit button until that Promise settles, then close it.
    const [submitting, setSubmitting] = useState(false)

    const show = useCallback(
        ({
             maxWidth = null,
             title = 'Oops!!',
             description = 'Something went wrong',
             closable = true,
             close = 'OK',
             checkbox= '',
             submit = 'Confirm',
             icon = <Icon.Delete fill={COLOR.u_red} size={30}/>,
             dropDown = '',
             type = 'error', // error, warning, success
             confirmText = '',
             onClose = () => {
             },
             onSubmit,
             className = props.className,
             isSync= false,
             checkboxText = '',
             asyncSubmit = false
         }) => {
            setSubmitting(false)
            setAlert({maxWidth, title, description, closable, icon, dropDown, type, close, submit, confirmText, onClose, onSubmit, className, checkbox, isSync, checkboxText, asyncSubmit})
            return props
        },
        [])
    const handleKeydown = (e) => {
        const classList = e.target.classList;
        if (e.keyCode === 13 && classList?.value !== 'ql-editor') handleAlert.submit()
    }
    useEffect(() => {
        document.addEventListener('keydown', handleKeydown, false)
        return () => document.removeEventListener('keydown', handleKeydown, false)
        // `submitting` is a dep so the Enter-key handler re-binds with a fresh closure and its
        // guard sees the in-flight state (otherwise a stale closure could re-submit mid-flight).
    }, [alert, submitting]);

    const handleAlert = {
        close: (e, flag) => {
            if (submitting) return; // don't let the user dismiss while an async submit is in flight
            if (alert?.closable) {
                setAlert(null)
                if (alert?.onClose) alert.onClose(e, flag)
                validator.current.hideMessages();
            }
        },
        submit: () => {
            if (submitting) return; // guard double-submit (button click + Enter key)
            const passedValidation = alert?.confirmText?.length !== 0 ? validator?.current?.allValid() : true;
            if (!passedValidation) {
                validator.current.showMessages();
                errorFocus();
                return;
            }
            // Opt-in async flow: keep the alert open with a loader until onSubmit's Promise settles.
            if (alert?.asyncSubmit) {
                const result = alert?.onSubmit ? alert.onSubmit(isSync) : undefined;
                validator.current.hideMessages();
                if (result && typeof result.then === 'function') {
                    setSubmitting(true);
                    const done = () => { setSubmitting(false); setAlert(null); };
                    Promise.resolve(result).then(done, done);
                } else {
                    setAlert(null);
                }
                return;
            }
            // Legacy synchronous flow — close first, then run onSubmit (unchanged).
            setAlert(null)
            if (alert?.onSubmit) alert.onSubmit(isSync)
            validator.current.hideMessages();
        }
    }

    const comment = useCallback(
        ({
            className = '',
            maxWidth = 600,
            type = '', // cancel, approve, reject
            title = 'Mention the comment below',
            description = '',
            fieldName = 'Comment',
            fieldValue = 'Comment',
            submit = 'OK',
            onSubmit = () => {
            },
            onClose = () => {
            },
            required = true,
            validator= true
        }) => {
            setCommentAlert({ className, maxWidth, type, title, description, fieldName, submit, onSubmit, fieldValue, onClose ,required, validator})
            return props
        },
        [])
    const handleCommentAlert = {
        close: (e, flag) => {
            if (commentAlert?.onClose) commentAlert.onClose(e, flag)
            setCommentAlert(null)
            validator.current.hideMessages();
        },
        submit: () => {
            if (validator?.current?.allValid()) {
                if (commentAlert?.onSubmit) commentAlert?.onSubmit(commentAlert?.fieldValue)
                setCommentAlert(null)
                validator.current.hideMessages();
            } else {
                validator.current.showMessages();
                errorFocus();
            }
        }
    }

    return (
        <Context.Provider value={{ show, comment }}>
            {props.children}
            {commentAlert && <Popup
                open={commentAlert}
                trigger={<></>}
                modal
                className="u_alert"
                onClose={handleCommentAlert?.close}
            >
                <div className={`u_alert ${commentAlert.className}`}>
                    <div className="u_alert-overlay" />
                    <div className="u_alert-content active" style={{ maxWidth: commentAlert?.maxWidth }}>

                        {/* header */}
                        <div className="u_alert-header">
                            <div className="u_alert-header-inner">
                                <h3>{commentAlert?.title}</h3>
                                <span className="u_alert-cancel" onClick={handleCommentAlert?.close}><Icon.Close fill={COLOR.u_blue} size={14} /></span>
                            </div>
                        </div>

                        {/* body */}
                        <div className="u_alert-body">
                            <div className="u_row">

                                {commentAlert?.description?.length !== 0 && <div className="u_col-12">
                                    <p className="u_pb-20">{commentAlert?.description}</p>
                                </div>}
                                <div className="u_col-12">
                                    <UInput label={commentAlert?.fieldName} textarea asterisk={commentAlert?.required}
                                        hasError={commentAlert?.validator ? validator.current.message(commentAlert?.fieldName, commentAlert?.fieldValue, `required|${valid.comments}`) : false}>
                                        <textarea
                                            className="u_textarea u_textarea-six-rows"
                                            placeholder=" "
                                            name={commentAlert?.fieldName}
                                            value={commentAlert?.fieldValue}
                                            onChange={(e) => setCommentAlert({ ...commentAlert, fieldValue: e.target.value })}
                                            autoComplete='off' />
                                    </UInput>
                                </div>

                            </div>
                        </div>

                        {/* footer */}
                        <div className="u_alert-footer">
                            <div className="u_alert-footer-inner-comment">
                                {commentAlert?.type === 'approve' ? <UButton
                                    type={commentAlert?.type === 'approve' ? 'approve' : commentAlert?.submit}
                                    variant={commentAlert?.type === 'approve' ? 'green' : 'blue'}
                                    onClick={handleCommentAlert?.submit}
                                /> : commentAlert?.type === 'reject' ? <UButton
                                    type={commentAlert?.type === 'reject' ? 'reject' : commentAlert?.submit}
                                    variant={commentAlert?.type === 'reject' ? 'red' : 'blue'}
                                    onClick={handleCommentAlert?.submit}
                                /> : commentAlert?.type === 'delete' ? <UButton
                                    type={commentAlert?.type === 'delete' ? 'delete' : commentAlert?.submit}
                                    variant={commentAlert?.type === 'delete' ? 'blue' : 'blue'}
                                    onClick={handleCommentAlert?.submit}
                                /> : commentAlert?.type === 'running' ? <UButton
                                    type={commentAlert?.type === 'running' ? 'running' : commentAlert?.submit}
                                    variant={commentAlert?.type === 'running' ? 'orange' : 'blue'}
                                    onClick={handleCommentAlert?.submit}
                                /> : commentAlert?.type === 'cancel' ? <UButton
                                    type={commentAlert?.type === 'cancel' ? 'cancel' : commentAlert?.submit}
                                    variant={commentAlert?.type === 'cancel' ? 'blue' : 'blue'}
                                    onClick={handleCommentAlert?.submit}
                                /> : <UButton
                                    type={commentAlert?.submit}
                                    variant="blue"
                                    onClick={handleCommentAlert?.submit}
                                />}
                            </div>
                        </div>
                    </div>
                </div>
            </Popup>}
            {alert && <Popup
                open={alert}
                trigger={<></>}
                modal
                className={`u_alert ${alert?.className}`}
                closeOnEscape={alert?.closable && !submitting}
                onClose={e => handleAlert?.close(e)}
            >
                <div className="u_alert">
                    <div className="u_alert-overlay" />
                    <div className="u_alert-content active" style={{ maxWidth: alert?.maxWidth }}>

                        {/* header */}
                        <div className="u_alert-header">
                            <div className="u_alert-header-inner">
                                <h3>{alert?.title}</h3>
                                {alert?.closable && !submitting && <span className="u_alert-cancel" onClick={e => handleAlert?.close(e)}><Icon.Close fill={COLOR.u_blue} size={14} /></span>}
                            </div>
                        </div>

                        {/* body */}
                        <div className="u_alert-body">
                            {alert?.icon && <div className={`u_alert-icon u_alert-icon-${alert?.type}`}>{alert?.icon}</div>}
                            <p className="u_alert-desc" dangerouslySetInnerHTML={{ __html: alert?.description }} />

                            {alert?.checkbox && <div className="u_flex u_align-center u_content-center"><UCheckBox name="isSync"
                                                          label={`${alert?.checkboxText ? alert?.checkboxText : "Do you want to apply the change for all the company?" }`}
                                                          id="isSync"
                                                          checked={isSync} htmlFor="isSync"
                                                          onChange={(e) => setIsSync(e?.target?.checked)}/>
                            </div>}
                            {alert?.dropDown && <div className="u_mt-10 u_flex u_align-center u_content-center">{alert?.dropDown}</div>}
                            {alert?.confirmText?.length !== 0 && <React.Fragment>
                                <p className="u_mb-15 u_text-center">Type "<strong className="u_color-red">{alert?.confirmText?.toUpperCase()}</strong>" to continue</p>
                                <UInput label="Type Here" asterisk
                                    hasError={validator.current.message('word', word, ['required', { in: [alert?.confirmText?.toUpperCase()] }], {
                                        messages: { required: `Please Type ${alert?.confirmText?.toUpperCase()}` }
                                    })}>
                                    <input type="text" name="word" maxLength={25} placeholder=" " autoComplete="off" value={word} onChange={e => setWord(e.target.value)} />
                                </UInput>
                            </React.Fragment>}
                        </div>

                        {/* footer */}
                        <div className="u_alert-footer">
                            <div className="u_alert-footer-inner">
                                {alert?.closable && <button type='button' className="u_btn white" disabled={submitting} onClick={e => handleAlert?.close(e, 'button')}>
                                    {(!alert?.onSubmit || alert?.close !== 'OK') ? alert?.close : 'Cancel'}</button>}
                                {alert?.onSubmit && <button type='submit' className='u_btn blue' disabled={submitting} onClick={handleAlert.submit}>
                                    {submitting
                                        ? <span className="u_flex u_align-center u_content-center u_gap-6"><Icon.Preloader color1={COLOR.u_white} color2={COLOR.u_white} size={16} />{alert?.submit}</span>
                                        : alert?.submit}</button>}
                            </div>
                        </div>
                    </div>
                </div>
            </Popup>}
        </Context.Provider>
    )
}

export default useUAlert
